import { useState, useEffect, useCallback } from 'react';
import { TourData, TourStorage, TourEvent } from '../components/OnboardingTour/types';
import { getNextTour } from '../components/OnboardingTour/tourConfigs';

const STORAGE_KEY = 'oogwai_onboarding_tours';

// Default storage structure
const defaultStorage: TourStorage = {
  completedTours: [],
  skippedTours: [],
  lastShown: {},
  viewCount: {},
};

// LocalStorage utilities
const getTourStorage = (): TourStorage => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? { ...defaultStorage, ...JSON.parse(data) } : defaultStorage;
  } catch (error) {
    console.warn('Failed to parse onboarding tour storage:', error);
    return defaultStorage;
  }
};

const saveTourStorage = (data: TourStorage): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save onboarding tour storage:', error);
  }
};

// Analytics tracking (optional - replace with your analytics service)
const trackTourEvent = (event: TourEvent, tourId: string, step?: number): void => {
  // Replace with your analytics tracking
  console.log('Tour Event:', { event, tourId, step, timestamp: new Date().toISOString() });
  
  // Example integration with common analytics services:
  // gtag('event', 'onboarding_tour', { event_category: event, event_label: tourId, value: step });
  // analytics.track('Onboarding Tour', { event, tourId, step });
};

interface UseOnboardingTourOptions {
  /**
   * Delay in milliseconds before showing the tour
   * @default 1000
   */
  delay?: number;

  /**
   * Whether to show the tour even if it was skipped before
   * @default false
   */
  showIfSkipped?: boolean;

  /**
   * Maximum number of times to show the tour
   * @default 3
   */
  maxViewCount?: number;

  /**
   * Whether to automatically start the tour on first visit
   * @default true
   */
  autoStart?: boolean;

  /**
   * Whether to automatically chain to the next tour in sequence when this tour completes
   * @default false
   */
  chainToNext?: boolean;
}

interface UseOnboardingTourReturn {
  /** Whether the tour should be running */
  runTour: boolean;
  
  /** Current tour data */
  currentTour: TourData | null;
  
  /** Start a specific tour */
  startTour: (tour: TourData) => void;
  
  /** Close/complete the current tour */
  closeTour: () => void;
  
  /** Skip the current tour */
  skipTour: () => void;
  
  /** Reset all tour data (for development/testing) */
  resetTours: () => void;
  
  /** Check if a specific tour has been completed */
  isTourCompleted: (tourId: string) => boolean;
  
  /** Check if a specific tour has been skipped */
  isTourSkipped: (tourId: string) => boolean;
  
  /** Get tour statistics */
  getTourStats: () => TourStorage;
}

export const useOnboardingTour = (
  tourToShow: TourData | null,
  options: UseOnboardingTourOptions = {}
): UseOnboardingTourReturn => {
  const {
    delay = 1000,
    showIfSkipped = false,
    maxViewCount = 3,
    autoStart = true,
    chainToNext = false,
  } = options;

  const [runTour, setRunTour] = useState(false);
  const [currentTour, setCurrentTour] = useState<TourData | null>(null);

  // Check if tour should be shown
  const shouldShowTour = useCallback((tour: TourData): boolean => {
    if (!tour) return false;

    const storage = getTourStorage();
    
    // Check if tour was completed
    if (storage.completedTours.includes(tour.id)) {
      return false;
    }
    
    // Check if tour was skipped and we don't want to show skipped tours
    if (!showIfSkipped && storage.skippedTours.includes(tour.id)) {
      return false;
    }
    
    // Check view count limit
    const viewCount = storage.viewCount[tour.id] || 0;
    if (viewCount >= maxViewCount) {
      return false;
    }
    
    return true;
  }, [showIfSkipped, maxViewCount]);

  // Start tour function
  const startTour = useCallback((tour: TourData) => {
    console.log('[TOUR DEBUG] startTour called for:', tour.id);

    if (!shouldShowTour(tour)) {
      console.log('[TOUR DEBUG] Tour should not show:', tour.id);
      return;
    }

    console.log('[TOUR DEBUG] Setting current tour and starting...', tour.id);
    setCurrentTour(tour);

    // Update view count
    const storage = getTourStorage();
    storage.viewCount[tour.id] = (storage.viewCount[tour.id] || 0) + 1;
    storage.lastShown[tour.id] = new Date().toISOString();
    saveTourStorage(storage);

    // Track analytics
    trackTourEvent('started', tour.id);

    if (delay > 0) {
      console.log(`[TOUR DEBUG] Delaying tour start by ${delay}ms`);
      setTimeout(() => {
        console.log('[TOUR DEBUG] Setting runTour to true');
        setRunTour(true);
      }, delay);
    } else {
      console.log('[TOUR DEBUG] Setting runTour to true immediately');
      setRunTour(true);
    }
  }, [shouldShowTour, delay]);

  // Close tour (completed)
  const closeTour = useCallback(() => {
    if (!currentTour) return;

    const storage = getTourStorage();

    // Mark as completed
    if (!storage.completedTours.includes(currentTour.id)) {
      storage.completedTours.push(currentTour.id);
    }

    // Remove from skipped if it was there
    storage.skippedTours = storage.skippedTours.filter(id => id !== currentTour.id);

    saveTourStorage(storage);
    trackTourEvent('completed', currentTour.id);

    setRunTour(false);
    setCurrentTour(null);

    // Check if we should chain to the next tour
    if (chainToNext) {
      const nextTour = getNextTour(currentTour.id);
      if (nextTour) {
        console.log(`Chaining to next tour: ${nextTour.id}`);

        // If next tour requires a specific path, navigate there first
        if (nextTour.requiredPath && window.location.pathname !== nextTour.requiredPath) {
          // Use window.location for navigation (works without router context)
          window.location.href = nextTour.requiredPath;
          // Note: Page will reload, tour will be triggered by event listener on new page
          return;
        } else {
          // Already on correct page, start next tour after a short delay
          setTimeout(() => {
            const event = new CustomEvent('startTour', { detail: nextTour });
            window.dispatchEvent(event);
          }, 500);
        }
      } else {
        console.log('Tour sequence completed - no more tours');
      }
    }
  }, [currentTour, chainToNext]);

  // Skip tour
  const skipTour = useCallback(() => {
    if (!currentTour) return;

    const storage = getTourStorage();
    
    // Mark as skipped
    if (!storage.skippedTours.includes(currentTour.id)) {
      storage.skippedTours.push(currentTour.id);
    }
    
    saveTourStorage(storage);
    trackTourEvent('skipped', currentTour.id);
    
    setRunTour(false);
    setCurrentTour(null);
  }, [currentTour]);

  // Reset all tours (for development/testing)
  const resetTours = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setRunTour(false);
    setCurrentTour(null);
  }, []);

  // Check if tour is completed
  const isTourCompleted = useCallback((tourId: string): boolean => {
    const storage = getTourStorage();
    return storage.completedTours.includes(tourId);
  }, []);

  // Check if tour is skipped
  const isTourSkipped = useCallback((tourId: string): boolean => {
    const storage = getTourStorage();
    return storage.skippedTours.includes(tourId);
  }, []);

  // Get tour statistics
  const getTourStats = useCallback((): TourStorage => {
    return getTourStorage();
  }, []);

  // Auto-start tour on mount if provided
  useEffect(() => {
    // Check if auto-start is globally enabled (from debug helper)
    const autoStartGloballyEnabled = localStorage.getItem('oogwai_tour_auto_start') !== 'false';

    console.log('[TOUR DEBUG] Auto-start check:', {
      tourId: tourToShow?.id,
      autoStart,
      autoStartGloballyEnabled,
      hasTour: !!tourToShow,
      shouldShow: tourToShow ? shouldShowTour(tourToShow) : false,
      storage: getTourStorage()
    });

    if (autoStart && autoStartGloballyEnabled && tourToShow && shouldShowTour(tourToShow)) {
      console.log('[TOUR DEBUG] Starting tour:', tourToShow.id);
      startTour(tourToShow);
    } else {
      console.log('[TOUR DEBUG] Not starting tour because:', {
        autoStart,
        autoStartGloballyEnabled,
        hasTour: !!tourToShow,
        shouldShow: tourToShow ? shouldShowTour(tourToShow) : false
      });
    }
  }, [tourToShow, autoStart, shouldShowTour, startTour]);

  return {
    runTour,
    currentTour,
    startTour,
    closeTour,
    skipTour,
    resetTours,
    isTourCompleted,
    isTourSkipped,
    getTourStats,
  };
};

// Utility hook for manual tour triggering
export const useTourTrigger = () => {
  const startManualTour = useCallback((tour: TourData) => {
    // Clear completed/skipped status and reset view count to allow replay
    const storage = getTourStorage();
    storage.completedTours = storage.completedTours.filter(id => id !== tour.id);
    storage.skippedTours = storage.skippedTours.filter(id => id !== tour.id);
    // Reset view count to allow unlimited manual replays
    delete storage.viewCount[tour.id];
    saveTourStorage(storage);

    const event = new CustomEvent('startTour', { detail: tour });
    window.dispatchEvent(event);
  }, []);

  const replayTour = useCallback((tourId: string) => {
    // Remove completed status and reset view count to allow replay
    const storage = getTourStorage();
    storage.completedTours = storage.completedTours.filter(id => id !== tourId);
    storage.skippedTours = storage.skippedTours.filter(id => id !== tourId);
    // Reset view count to allow unlimited replays
    delete storage.viewCount[tourId];
    saveTourStorage(storage);

    // Trigger page reload to restart tour
    window.location.reload();
  }, []);

  return {
    startManualTour,
    replayTour,
  };
};

export default useOnboardingTour;